import { DesignData, MediaElement, Page, Rect, SlotLayout } from 'editor/src/store/design/types';

import limitPrecision from 'editor/src/util/limitPrecision';

// Per-axis size limits in mm, as configured for the product (the platform's
// "Custom sizing" rules). There is deliberately no default: a product whose
// limits are unknown is not resizable by the buyer.
export interface DesignSizeBoundsMm {
  width: { minMm: number; maxMm: number };
  height: { minMm: number; maxMm: number };
}

/**
 * Cover-fit an image inside its (resized) frame, preserving the image's own
 * aspect ratio (derived from the current pw/ph, which are uniform-scaled from
 * the natural image dimensions) and centering it — same semantics as
 * getCenteredImagePosition, but without needing the gallery asset.
 */
function coverFitImage(frameWidth: number, frameHeight: number, imageAspect: number) {
  let pw;
  let ph;
  if (frameWidth / frameHeight > imageAspect) {
    pw = frameWidth;
    ph = frameWidth / imageAspect;
  } else {
    ph = frameHeight;
    pw = frameHeight * imageAspect;
  }
  return {
    px: limitPrecision((frameWidth - pw) / 2),
    py: limitPrecision((frameHeight - ph) / 2),
    pw: limitPrecision(pw),
    ph: limitPrecision(ph),
  };
}

function resizeCoordinate(value: number, origin: number, oldSize: number, newSize: number) {
  const relative = value - origin;
  if (relative < 0) {
    return value;
  }
  if (relative > oldSize) {
    return value + newSize - oldSize;
  }
  return origin + (relative * newSize) / oldSize;
}

type CoordinateMap = (value: number) => number;

function resizeRect(rect: Rect, mapX: CoordinateMap, mapY: CoordinateMap): Rect {
  return {
    x: limitPrecision(mapX(rect.x)),
    y: limitPrecision(mapY(rect.y)),
    width: limitPrecision(mapX(rect.x + rect.width) - mapX(rect.x)),
    height: limitPrecision(mapY(rect.y + rect.height) - mapY(rect.y)),
  };
}

function resizeMediaElement(element: MediaElement, mapX: CoordinateMap, mapY: CoordinateMap): MediaElement {
  if (element.type === 'line') {
    return {
      ...element,
      x1: limitPrecision(mapX(element.x1)),
      y1: limitPrecision(mapY(element.y1)),
      x2: limitPrecision(mapX(element.x2)),
      y2: limitPrecision(mapY(element.y2)),
    };
  }

  const frame = resizeRect(element, mapX, mapY);

  if (element.type === 'image' || element.type === 'addon') {
    // pw/ph are uniform-scaled natural image dimensions — their ratio is the
    // image aspect. Re-cover-fit so the artwork fills the new frame without
    // distortion instead of stretching per-axis.
    const imageAspect = element.pw && element.ph ? element.pw / element.ph : frame.width / frame.height;
    return { ...element, ...frame, ...coverFitImage(frame.width, frame.height, imageAspect) };
  }

  return { ...element, ...frame };
}

// Resize around the trim origin. Coordinates in the bleed retain their
// physical distance from the corresponding trim edge.
function getCoordinateMaps(
  page: Page,
  newTrimWidth: number,
  newTrimHeight: number,
  oldTrimWidth: number,
  oldTrimHeight: number,
) {
  const trim = page.groups.content?.find((element) => element.type === 'area');
  return {
    mapX: (value: number) => resizeCoordinate(value, trim?.x ?? 0, oldTrimWidth, newTrimWidth),
    mapY: (value: number) => resizeCoordinate(value, trim?.y ?? 0, oldTrimHeight, newTrimHeight),
  };
}

// Product layouts sit on the first page; their frames move with the layers inside them, and
// the gap takes the smaller axis ratio so a re-cut grid lands close to the moved frames.
function resizeSlotLayouts(
  layouts: SlotLayout[],
  page: Page,
  newTrimWidth: number,
  newTrimHeight: number,
  oldTrimWidth: number,
  oldTrimHeight: number,
): SlotLayout[] {
  const { mapX, mapY } = getCoordinateMaps(page, newTrimWidth, newTrimHeight, oldTrimWidth, oldTrimHeight);
  const ratio = Math.min(newTrimWidth / oldTrimWidth, newTrimHeight / oldTrimHeight);
  return layouts.map((layout) => ({
    ...layout,
    area: resizeRect(layout.area, mapX, mapY),
    gap: limitPrecision(layout.gap * ratio),
    frames: layout.frames.map((frame) => ({ ...frame, ...resizeRect(frame, mapX, mapY) })),
  }));
}

function resizePage(
  page: Page,
  newTrimWidth: number,
  newTrimHeight: number,
  oldTrimWidth: number,
  oldTrimHeight: number,
): Page {
  // Absolute deltas keep bleed / safe-area margins at their physical size
  // (a 3mm bleed must stay 3mm at any product size).
  const deltaW = newTrimWidth - oldTrimWidth;
  const deltaH = newTrimHeight - oldTrimHeight;
  const { mapX, mapY } = getCoordinateMaps(page, newTrimWidth, newTrimHeight, oldTrimWidth, oldTrimHeight);

  const groups: Page['groups'] = { ...page.groups };

  // Structural rectangles (bleed, safe area, mediabox, content, …) are anchored
  // by absolute margins from the page edges: keep x/y, stretch width/height.
  (['bleed', 'safe_area', 'mediabox', 'content', 'blank'] as const).forEach((key) => {
    const elements = groups[key];
    if (elements) {
      groups[key] = elements.map((element: any) => ({
        ...element,
        width: limitPrecision(element.width + deltaW),
        height: limitPrecision(element.height + deltaH),
      }));
    }
  });

  if (groups.media) {
    groups.media = groups.media.map((element) => resizeMediaElement(element, mapX, mapY));
  }

  return {
    ...page,
    width: limitPrecision(page.width + deltaW),
    height: limitPrecision(page.height + deltaH),
    groups,
  };
}

/**
 * Resize a (flat, single-product) design to a new trim size in mm. Page and
 * structural group dimensions grow by the absolute delta so bleed and safe
 * margins keep their physical size; media elements scale proportionally and
 * images are re-cover-fitted to their new frames.
 *
 * `bounds` are the product's configured limits; they are stamped onto
 * designData.dimensions so the saved structure states the range the size was
 * chosen from instead of an invented default.
 *
 * Returns a new DesignData; the input is not mutated.
 */
function resizeDesignToDimensions(
  designData: DesignData,
  widthMm: number,
  heightMm: number,
  bounds: DesignSizeBoundsMm,
): DesignData {
  const firstPage = designData.spreads[0]?.pages[0];
  const trim = firstPage?.groups.content?.find((element) => element.type === 'area');
  const oldTrimWidth = designData.dimensions?.width ?? (trim?.width || firstPage?.width);
  const oldTrimHeight = designData.dimensions?.height ?? (trim?.height || firstPage?.height);
  if (!oldTrimWidth || !oldTrimHeight) {
    return designData;
  }

  return {
    ...designData,
    dimensions: {
      min_width: bounds.width.minMm,
      min_height: bounds.height.minMm,
      max_width: bounds.width.maxMm,
      max_height: bounds.height.maxMm,
      max_surface: designData.dimensions?.max_surface,
      width: widthMm,
      height: heightMm,
    },
    spreads: designData.spreads.map((spread) => ({
      ...spread,
      pages: spread.pages.map((page) => resizePage(page, widthMm, heightMm, oldTrimWidth, oldTrimHeight)),
      ...(spread.slotLayouts
        ? {
            slotLayouts: resizeSlotLayouts(
              spread.slotLayouts,
              spread.pages[0],
              widthMm,
              heightMm,
              oldTrimWidth,
              oldTrimHeight,
            ),
          }
        : {}),
    })),
  };
}

export default resizeDesignToDimensions;
